/**
 * Offline Verification Session Service
 * Manages the lifecycle of offline verification sessions in IndexedDB.
 * A session is the atomic unit that bundles all verification evidence for offline completion.
 *
 * Session status flow:
 *   preflight → ready → in_progress → evidence_complete → signed → syncing → synced | sync_failed
 */

import indexedDBHelper from '../utils/indexedDBHelper';
import { hashObject, chainHash, computeSessionIntegrity } from '../utils/integrityChain';

// Debounce timer for session writes
let _writeTimer = null;
const WRITE_DEBOUNCE_MS = 500;

/**
 * Generate a unique session ID
 */
function generateSessionId() {
  const ts = Date.now();
  const rand = Math.random().toString(36).substring(2, 10);
  return `ovs_${ts}_${rand}`;
}

/**
 * Create a new offline verification session from preflight data
 * @param {string|number} verificationId - Server-assigned verification ID
 * @param {Object} preflightData - Cached data fetched while online
 * @param {Object} preflightData.verification - Full verification record
 * @param {Object} preflightData.project - Project details
 * @param {Object} preflightData.milestone - Milestone (if linked)
 * @param {{lat: number, lng: number, radius: number}} preflightData.siteCoordinates
 * @param {Array} preflightData.checklist - Checklist items
 * @param {Array} [preflightData.buildingCodes] - Building codes (professional tier)
 * @param {Object} preflightData.verifierProfile - Verifier name, license, tier
 * @param {string} preflightData.serverTime - Server timestamp from X-Server-Time header
 * @returns {Promise<Object>} The created session
 */
export async function createSession(verificationId, preflightData) {
  const now = new Date().toISOString();
  const deviceTimestamp = Date.now();
  const serverTime = preflightData.serverTime || now;

  const session = {
    sessionId: generateSessionId(),
    verificationId: String(verificationId),
    verifierId: preflightData.verifierProfile?.userId || null,
    verificationTier: preflightData.verification?.tier || 'general',

    preflight: {
      verification: preflightData.verification || {},
      project: preflightData.project || {},
      milestone: preflightData.milestone || null,
      siteCoordinates: preflightData.siteCoordinates || { lat: 0, lng: 0, radius: 100 },
      checklist: preflightData.checklist || [],
      buildingCodes: preflightData.buildingCodes || [],
      verifierProfile: preflightData.verifierProfile || {},
      cachedAt: now,
      serverTimeAtCache: serverTime
    },

    geofenceProofs: [],

    photos: [],

    voiceNotes: [],

    checklistState: {
      items: (preflightData.checklist || []).map((item, idx) => ({
        id: item.id || `checklist_${idx}`,
        description: item.description || item.text || item.label || '',
        required: item.required !== false,
        checked: false,
        result: null, // pass | fail | na
        notes: '',
        photoIds: [],
        voiceNoteIds: [],
        completedAt: null
      })),
      completedCount: 0,
      totalCount: (preflightData.checklist || []).length
    },

    verifierNotes: '',

    draftReport: {
      executiveSummary: '',
      findings: '',
      recommendations: '',
      overallAssessment: null, // pass | fail | conditional
      complianceData: {},
      deficiencies: []
    },

    signature: null,

    status: 'preflight',
    startedAt: null,
    completedAt: null,

    integrity: {
      version: 1,
      algorithm: 'SHA-256',
      chainHashes: [],
      finalHash: null,
      deviceInfo: {
        platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        isNative: typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() || false
      },
      clockDrift: {
        serverTimeAtPreflight: serverTime,
        deviceTimeAtPreflight: now,
        estimatedDriftMs: new Date(serverTime).getTime() - deviceTimestamp
      }
    },

    sync: {
      attempts: 0,
      lastAttemptAt: null,
      lastError: null,
      uploadedPhotoIds: [],
      uploadedVoiceNoteIds: [],
      serverAcknowledged: false,
      serverSessionId: null
    }
  };

  // Mark ready after preflight
  session.status = 'ready';

  await indexedDBHelper.putSession(session);
  return session;
}

/**
 * Get a session by its session ID
 */
export async function getSession(sessionId) {
  return indexedDBHelper.getSession(sessionId);
}

/**
 * Get the active (non-synced) session for a verification
 */
export async function getSessionByVerification(verificationId) {
  return indexedDBHelper.getSessionByVerification(String(verificationId));
}

/**
 * Update a session (debounced write to IndexedDB)
 * @param {string} sessionId
 * @param {Object} updates - Partial updates to merge
 * @param {boolean} [immediate=false] - Skip debounce
 */
export async function updateSession(sessionId, updates, immediate = false) {
  const session = await indexedDBHelper.getSession(sessionId);
  if (!session) throw new Error(`Session ${sessionId} not found`);

  const updated = { ...session, ...updates };

  if (immediate) {
    await indexedDBHelper.putSession(updated);
    return updated;
  }

  // Debounced write
  clearTimeout(_writeTimer);
  return new Promise((resolve) => {
    _writeTimer = setTimeout(async () => {
      await indexedDBHelper.putSession(updated);
      resolve(updated);
    }, WRITE_DEBOUNCE_MS);
  });
}

/**
 * Start a verification session (move from ready → in_progress)
 */
export async function startSession(sessionId) {
  return updateSession(sessionId, {
    status: 'in_progress',
    startedAt: new Date().toISOString()
  }, true);
}

/**
 * Add a geofence proof to the session's chain
 * @param {string} sessionId
 * @param {Object} proofData - { location, siteCoordinates, distanceMeters, zone, antiSpoofing }
 * @returns {Promise<Object>} The proof with hash
 */
export async function addGeofenceProof(sessionId, proofData) {
  const session = await indexedDBHelper.getSession(sessionId);
  if (!session) throw new Error(`Session ${sessionId} not found`);

  const proof = {
    id: `gp_${session.geofenceProofs.length}`,
    timestamp: new Date().toISOString(),
    deviceTimestamp: Date.now(),
    ...proofData,
    hash: null
  };

  // Hash the proof data (excluding the hash field itself)
  const { hash: _, ...proofWithoutHash } = proof;
  proof.hash = await hashObject(proofWithoutHash);

  // Chain hash: link to previous proof
  const previousChainHash = session.integrity.chainHashes.length > 0
    ? session.integrity.chainHashes[session.integrity.chainHashes.length - 1]
    : '';
  const newChainHash = await chainHash(proof.hash, previousChainHash);

  session.geofenceProofs.push(proof);
  session.integrity.chainHashes.push(newChainHash);

  await indexedDBHelper.putSession(session);
  return proof;
}

/**
 * Add a photo reference to the session
 * @param {string} sessionId
 * @param {Object} photoRecord - { photoId, capturedAt, location, checklistItemId, caption, hash }
 */
export async function addPhoto(sessionId, photoRecord) {
  const session = await indexedDBHelper.getSession(sessionId);
  if (!session) throw new Error(`Session ${sessionId} not found`);

  session.photos.push(photoRecord);

  // Chain the photo hash
  const previousChainHash = session.integrity.chainHashes.length > 0
    ? session.integrity.chainHashes[session.integrity.chainHashes.length - 1]
    : '';
  const newChainHash = await chainHash(photoRecord.hash, previousChainHash);
  session.integrity.chainHashes.push(newChainHash);

  await indexedDBHelper.putSession(session);
  return photoRecord;
}

/**
 * Add a voice note reference to the session
 * @param {string} sessionId
 * @param {Object} voiceNoteRecord - { id, capturedAt, location, checklistItemId, durationSeconds, hash }
 */
export async function addVoiceNote(sessionId, voiceNoteRecord) {
  const session = await indexedDBHelper.getSession(sessionId);
  if (!session) throw new Error(`Session ${sessionId} not found`);

  session.voiceNotes.push(voiceNoteRecord);

  // Chain the voice note hash
  const previousChainHash = session.integrity.chainHashes.length > 0
    ? session.integrity.chainHashes[session.integrity.chainHashes.length - 1]
    : '';
  const newChainHash = await chainHash(voiceNoteRecord.hash, previousChainHash);
  session.integrity.chainHashes.push(newChainHash);

  await indexedDBHelper.putSession(session);
  return voiceNoteRecord;
}

/**
 * Update checklist state
 */
export async function updateChecklist(sessionId, checklistState) {
  const session = await indexedDBHelper.getSession(sessionId);
  if (!session) throw new Error(`Session ${sessionId} not found`);

  session.checklistState = {
    ...checklistState,
    completedCount: checklistState.items.filter(i => i.checked).length,
    totalCount: checklistState.items.length
  };

  await indexedDBHelper.putSession(session);
  return session.checklistState;
}

/**
 * Set draft report content
 */
export async function setDraftReport(sessionId, report) {
  return updateSession(sessionId, { draftReport: report });
}

/**
 * Set verifier notes
 */
export async function setVerifierNotes(sessionId, notes) {
  return updateSession(sessionId, { verifierNotes: notes });
}

/**
 * Capture signature and move to 'signed' status
 * @param {string} sessionId
 * @param {Object} signatureData - { signatureData, signatureType, fullName }
 */
export async function setSignature(sessionId, signatureData) {
  const session = await indexedDBHelper.getSession(sessionId);
  if (!session) throw new Error(`Session ${sessionId} not found`);

  const sigHash = await hashObject({
    signatureData: signatureData.signatureData,
    signatureType: signatureData.signatureType,
    fullName: signatureData.fullName
  });

  session.signature = {
    ...signatureData,
    signedAt: new Date().toISOString(),
    hash: sigHash
  };

  // Chain the signature hash
  const previousChainHash = session.integrity.chainHashes.length > 0
    ? session.integrity.chainHashes[session.integrity.chainHashes.length - 1]
    : '';
  const newChainHash = await chainHash(sigHash, previousChainHash);
  session.integrity.chainHashes.push(newChainHash);

  session.status = 'signed';

  await indexedDBHelper.putSession(session);
  return session;
}

/**
 * Complete the session: compute the final integrity hash and mark as ready to sync
 * @param {string} sessionId
 * @returns {Promise<Object>} The completed session with finalHash
 */
export async function completeSession(sessionId) {
  const session = await indexedDBHelper.getSession(sessionId);
  if (!session) throw new Error(`Session ${sessionId} not found`);

  session.completedAt = new Date().toISOString();
  session.status = 'signed';

  // Compute final integrity hash over the entire session
  session.integrity.finalHash = await computeSessionIntegrity(session);

  await indexedDBHelper.putSession(session);
  return session;
}

/**
 * Get all sessions pending sync
 */
export async function getPendingSessions() {
  const all = await indexedDBHelper.getAllSessions();
  return all.filter(s => s.status === 'signed' || s.status === 'sync_failed');
}

/**
 * Update sync state for a session
 */
export async function updateSyncState(sessionId, syncUpdates) {
  const session = await indexedDBHelper.getSession(sessionId);
  if (!session) throw new Error(`Session ${sessionId} not found`);

  session.sync = { ...session.sync, ...syncUpdates };
  if (syncUpdates.serverAcknowledged) {
    session.status = 'synced';
  }

  await indexedDBHelper.putSession(session);
  return session;
}

/**
 * Mark a session as syncing
 */
export async function markSyncing(sessionId) {
  return updateSession(sessionId, { status: 'syncing' }, true);
}

/**
 * Mark a session sync as failed
 */
export async function markSyncFailed(sessionId, error) {
  const session = await indexedDBHelper.getSession(sessionId);
  if (!session) throw new Error(`Session ${sessionId} not found`);

  session.status = 'sync_failed';
  session.sync.attempts += 1;
  session.sync.lastAttemptAt = new Date().toISOString();
  session.sync.lastError = typeof error === 'string' ? error : error?.message || 'Unknown error';

  await indexedDBHelper.putSession(session);
  return session;
}

/**
 * Delete a session and its associated evidence from IndexedDB
 */
export async function deleteSession(sessionId) {
  const session = await indexedDBHelper.getSession(sessionId);
  if (!session) return;

  // Delete associated voice notes
  const voiceNotes = await indexedDBHelper.getVoiceNotesBySession(sessionId);
  for (const vn of voiceNotes) {
    await indexedDBHelper.deleteVoiceNote(vn.id);
  }

  // Delete associated photos
  const photos = await indexedDBHelper.getPhotosBySession(sessionId);
  for (const photo of photos) {
    await indexedDBHelper.deletePhoto(photo.id);
  }

  await indexedDBHelper.deleteSession(sessionId);
}

export default {
  createSession,
  getSession,
  getSessionByVerification,
  startSession,
  updateSession,
  addGeofenceProof,
  addPhoto,
  addVoiceNote,
  updateChecklist,
  setDraftReport,
  setVerifierNotes,
  setSignature,
  completeSession,
  getPendingSessions,
  updateSyncState,
  markSyncing,
  markSyncFailed,
  deleteSession
};
